import React from 'react'
import * as H from 'history'
import queryString from 'query-string'

import { Site, siteAds } from 'thewest/Site'

import { registerPageRoutes } from 'common/routing/route-resolver'
import { PageResolver } from 'common/routing/page-resolver'

import { layout } from 'thewest/App.routing'
import { staticRoutes } from 'thewest/routes'
import {
    notFoundRoute,
    matchedSomethingWentWrongRoute,
    staticSomethingWentWrongRoute,
} from 'thewest/routes/errors'

import './App.css'

import { DataLoaderGlobalParams } from 'common/data/resources'
import { getSearchPageRouteInfo } from './routes/search/get-search-page-route-info'
import { getProfilePageRouteInfo } from './routes/profile/get-profile-page-route-info'
import { createArticleRoute } from './routes/publication/kind/new-article'
import { createGalleryRoute } from './routes/publication/kind/new-gallery'
import { getRedirectRouteInfo } from 'common/routing/get-redirect-route-info'
import { getTopicPageRouteInfo } from './routes/topic/get-topic-page-route-info'
import { createFeatureRoute } from './routes/publication/kind/feature'
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { ProvideRouteResolvedDataToTheme } from 'component-library/routing/provide-route-resolved-data-to-theme'
import { regionalCanonicalMeta } from 'component-library/templates/Publication/lib/get-canonical-link'
import { Product } from 'component-library/__product'
import { createDiagPage } from './routes/diag-routes'
import { matchedErrorRoutePages } from 'component-library/diagnostics/error-routes'
import { errorLocation } from 'common/server/common-routes'
import { StaticRoutes } from 'common/routing/page-definition'
import { RenderTargetContext } from 'common/context-providers/render-target-context'
import { getSectionMeta } from 'common/routing/use-section-meta'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { AuthenticationHandler } from 'component-library/user-registration/authentication/AuthenticationHandler'
import { PageAdditionalProps } from 'page-lifecycle-provider'
import { createCookieSettingsPage } from './routes/cookie-consent'
import { AllEvents } from 'common/events/all-events'

export interface Props {
    hostname: string | undefined
    protocol: string | undefined
    services: DataLoaderGlobalParams
    onEvent: (event: AllEvents) => void
    location: H.Location
}

export function initRouting() {
    registerPageRoutes<typeof layout, TheWestSection>({
        staticRoutes,
        matchRoutes: {
            // TODO Cleanup the double function call
            '/search$': services =>
                getSearchPageRouteInfo(queryString.parse(services.location.search))(services),
            '/diag$': ({ config, resolution }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'
                return createDiagPage(config, section as TheWestSection)
            },
            '/cookie-settings$': ({ resolution, store }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'

                return createCookieSettingsPage(store, section as TheWestSection)
            },
            // The following are required for regional error page matching if you want to explicitly view a regional something went wrong page
            [`${errorLocation}$`]: ({ config, getAdTargeting, resolution }) =>
                matchedSomethingWentWrongRoute(config, getAdTargeting, resolution),
            ...(matchedErrorRoutePages as StaticRoutes<typeof layout, TheWestSection>),
        },
        serverRoutes: {
            profile: ({ config, getAdTargeting, resolution, location, store }) => {
                return getProfilePageRouteInfo({
                    profile: resolution.resolution.profile,
                    section: resolution.resolution.section as TheWestSection,
                    config,
                    getAdTargeting,
                    location,
                    store,
                })
            },
            redirect: ({ resolution }) => {
                return getRedirectRouteInfo(resolution.resolution.redirectTo.targetUrl)
            },
            topic: ({ resolution, config, getAdTargeting }) => {
                return getTopicPageRouteInfo({
                    publicationKind: resolution.resolution.publicationKind,
                    topic: resolution.resolution.topic,
                    config,
                    section: resolution.resolution.section as TheWestSection,
                    getAdTargeting,
                })
            },
            publication: ({ resolution, config, store, getAdTargeting, renderTarget }) => {
                const featureState = store.getState().toggles
                const regionalMeta = isFeatureEnabled(featureState, 'regionals', false)
                    ? regionalCanonicalMeta(
                          config,
                          store.getState().meta.sectionMeta,
                          resolution.resolution.publication
                      )
                    : undefined
                const sectionMeta = getSectionMeta(
                    store.getState().meta.sectionMeta,
                    resolution.resolution.section as TheWestSection
                )

                if (resolution.resolution.publication.kind === 'article') {
                    const shouldResolveFeatureRoute = isFeatureEnabled(
                        featureState,
                        'immersive-story-article-template'
                    )

                    if (
                        resolution.resolution.publication.layoutView === 'feature' &&
                        shouldResolveFeatureRoute
                    ) {
                        return createFeatureRoute({
                            config,
                            article: resolution.resolution.publication,
                            section: resolution.resolution.section as TheWestSection,
                            getAdTargeting,
                            sectionMeta,
                            pageMeta: regionalMeta,
                        })
                    }

                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution.section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                        renderTarget,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        config,
                        gallery: resolution.resolution.publication,
                        numberGalleryItems: resolution.resolution.publication.imageCount,
                        section: resolution.resolution.section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                    })
                }
                if (
                    resolution.resolution.publication.kind === 'redirect' &&
                    resolution.resolution.publication.redirectUrl
                ) {
                    return getRedirectRouteInfo(resolution.resolution.publication.redirectUrl)
                }

                return null
            },
            preview: ({ resolution, config, getAdTargeting, renderTarget }) => {
                if (resolution.resolution.publication.kind === 'article') {
                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution.section as TheWestSection,
                        getAdTargeting,
                        renderTarget,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        config,
                        gallery: resolution.resolution.publication,
                        numberGalleryItems: resolution.resolution.publication.imageCount,
                        section: resolution.resolution.section as TheWestSection,
                        getAdTargeting,
                    })
                }

                return null
            },
        },
        errorRouteInfo: staticSomethingWentWrongRoute,
        notFoundRouteInfo: notFoundRoute,
    })
}

initRouting()

export const App: React.FC<Props> = ({
    onEvent,
    hostname,
    protocol: proto,
    location,
    services,
}) => {
    const { renderTarget } = React.useContext(RenderTargetContext)

    return (
        <PageResolver
            stretchColumns={false}
            layout={layout}
            hostname={hostname}
            protocol={proto}
            location={location}
            onEvent={onEvent}
            siteAds={siteAds}
            services={services}
            renderTarget={renderTarget}
            renderPage={(pageContents, ads, pageType, section, additionalPageProperties) => (
                <ProvideRouteResolvedDataToTheme product={Product.TheWest} section={section}>
                    <PageAdditionalProps
                        pageProperties={{
                            marginSize: 'smaller-margins',
                            headlineStyle: 'serif-headlines',
                        }}
                    />
                    <AuthenticationHandler
                        logger={services.log}
                        config={services.config}
                        onEvent={onEvent}
                    />
                    <Site
                        pageType={pageType}
                        section={section as TheWestSection}
                        additionalPageProperties={additionalPageProperties}
                        config={services.config}
                        onEvent={onEvent}
                        pageContents={pageContents}
                        location={location}
                        ads={ads}
                    />
                </ProvideRouteResolvedDataToTheme>
            )}
        />
    )
}
